import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Flag, Loader2 } from "lucide-react";
import { StatusBadge } from "@/components/StatusBadge";
import { Panel, KV } from "@/components/admin/Panel";
import { IdDocs } from "@/components/admin/IdDocs";
import { VettingPanel } from "@/components/admin/VettingPanel";
import { InterviewPanel } from "@/components/admin/InterviewPanel";
import { PaymentPanel } from "@/components/admin/PaymentPanel";
import { IntroPanel } from "@/components/admin/IntroPanel";
import { NotesPanel, HistoryPanel } from "@/components/admin/NotesPanel";
import { useSiteSettings } from "@/context/SettingsContext";
import { api, fmtDate, formatErr, money } from "@/lib/api";
import { ADDON_LABELS } from "@/data/options";

const STEPS = ["New", "ID Under Review", "Interview Scheduled", "Interviewed", "Approved", "Cash Payment Received", "Active Member"];

const Pipeline = ({ status }) => {
  const idx = STEPS.indexOf(status);
  return (
    <div className="flex flex-wrap gap-2 mb-6" data-testid="status-pipeline">
      {STEPS.map((s, i) => (
        <span key={s} className={`rounded-full px-3 py-1 text-xs font-bold border ${status === "Declined" ? "border-border text-muted-foreground" : i <= idx ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground"}`}>{s}</span>
      ))}
      {status === "Declined" && <span className="rounded-full px-3 py-1 text-xs font-bold bg-red-500 text-white">Declined</span>}
    </div>
  );
};

export default function ApplicantDetail() {
  const { id } = useParams();
  const { settings } = useSiteSettings();
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/admin/applications/${id}`);
      setData(d);
    } catch (e) {
      toast.error(formatErr(e));
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (!data) return <div className="grid place-items-center py-32"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  const a = data.application, c = a.contact, p = a.profile;
  const tier = a.tier === "custom" && a.custom_tier ? { ...a.custom_tier, key: "custom" } : settings?.tiers.find((t) => t.key === a.tier);

  return (
    <div data-testid="applicant-detail-page">
      <Link to="/admin/applicants" className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground mb-4" data-testid="back-to-applicants"><ArrowLeft className="h-4 w-4" />All applicants</Link>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" data-testid="applicant-name">{c.full_name}</h1>
        <StatusBadge status={a.status} testId="applicant-status-badge" />
        {a.red_flag && <span className="inline-flex items-center gap-1 rounded-full bg-red-500 text-white px-2.5 py-0.5 text-xs font-bold" data-testid="applicant-red-flag-badge"><Flag className="h-3 w-3" />Red flag</span>}
      </div>
      <Pipeline status={a.status} />
      <div className="grid gap-6 xl:grid-cols-3">
        <div className="xl:col-span-2 space-y-6">
          <Panel title="Contact" testId="panel-contact">
            <KV rows={[["Email", c.email], ["Phone", `${c.phone_code} ${c.phone}`], ["Country", c.country], ["City", c.city], ["Time zone", c.timezone], ["Submitted", fmtDate(a.created_at)]]} />
          </Panel>
          <Panel title="Buyer profile & membership" testId="panel-profile">
            <KV rows={[["Business", p.business_name], ["Category", p.category], ["Monthly volume", p.monthly_volume], ["Years in business", p.years_in_business], ["Links", p.links], ["Heard about us", p.heard_about], ["Tier", tier ? `${tier.name} · ${money(tier.price)}` : a.tier], ["Add-on interest", a.addons.map((x) => ADDON_LABELS[x]).join(", ") || "None"]]} />
          </Panel>
          <IdDocs files={data.files} onChange={load} />
          {a.status === "Active Member" && <IntroPanel app={a} intros={data.introductions} limit={data.intro_limit} onChange={load} />}
          {data.addon_requests.length > 0 && (
            <Panel title="Add-on quote requests" testId="panel-addon-requests">
              {data.addon_requests.map((r) => (
                <div key={r.id} className="rounded-xl border border-border p-3 text-sm mb-2">
                  <p className="font-bold">{r.services.map((s) => ADDON_LABELS[s]).join(", ")}</p>
                  <p className="text-muted-foreground whitespace-pre-wrap">{r.details || "-"}</p>
                  <p className="text-xs text-muted-foreground mt-1">{fmtDate(r.created_at)}</p>
                </div>
              ))}
            </Panel>
          )}
          <NotesPanel app={a} onChange={load} />
        </div>
        <div className="space-y-6">
          <VettingPanel key={`${a.id}-${a.updated_at}`} app={a} limit={data.intro_limit} onSaved={setData} />
          <InterviewPanel app={a} onSaved={setData} />
          <PaymentPanel app={a} defaultAmount={tier?.price} onSaved={setData} />
          <HistoryPanel app={a} />
        </div>
      </div>
    </div>
  );
}
